/**
 * TanStack Query hooks for `cohort-analysis`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	createCohort,
	createEvent,
	getCohort,
	getMatrix,
	listCohorts,
	upsertMember,
	cohortAnalysisQueryKeys,
	type CreateCohortBody,
	type CreateCohortResponse,
	type CreateEventBody,
	type CreateEventResponse,
	type GetCohortResponse,
	type ListCohortsResponse,
	type MatrixResponse,
	type UpsertMemberBody,
	type UpsertMemberResponse,
} from "./api";

export function useCohortList(): UseQueryResult<ListCohortsResponse, Error> {
	return useQuery({
		queryKey: cohortAnalysisQueryKeys.list(),
		queryFn: listCohorts,
	});
}

export function useCohortDetail(
	id: string | null,
): UseQueryResult<GetCohortResponse, Error> {
	return useQuery({
		queryKey: cohortAnalysisQueryKeys.detail(id ?? ""),
		queryFn: () => getCohort(id as string),
		enabled: !!id,
	});
}

export function useCohortMatrix(
	id: string | null,
): UseQueryResult<MatrixResponse, Error> {
	return useQuery({
		queryKey: cohortAnalysisQueryKeys.matrix(id ?? ""),
		queryFn: () => getMatrix(id as string),
		enabled: !!id,
	});
}

export function useCreateCohort(): UseMutationResult<
	CreateCohortResponse,
	Error,
	CreateCohortBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createCohort,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: cohortAnalysisQueryKeys.all });
		},
	});
}

export function useUpsertMember(
	cohortId: string,
): UseMutationResult<UpsertMemberResponse, Error, UpsertMemberBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: UpsertMemberBody) => upsertMember(cohortId, body),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: cohortAnalysisQueryKeys.matrix(cohortId),
			});
		},
	});
}

export function useCreateEvent(
	cohortId: string,
): UseMutationResult<CreateEventResponse, Error, CreateEventBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateEventBody) => createEvent(cohortId, body),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: cohortAnalysisQueryKeys.matrix(cohortId),
			});
		},
	});
}
